// Pieces of the staff "Blocked at intake" screen with no JSX: endpoints,
// ranges, category colours, request params, the URL's filters and the
// numbers. In a `.js` file so `react-refresh/only-export-components` stays
// quiet, same as components/staff-leads/model.js.
//
// Shape of what the two endpoints return: BlockedOut and BlockedDetailOut in
// the backend's leads/blocked_api.py, computed by leads/blocked_at_intake.py.
// Two scales to keep apart, as on the Home: every `delta_pct` is already a
// percentage (12.3 = +12.3%), every `pct`/`rate`/`block_rate` is a fraction
// (0.123 = 12.3%). Both routes use `exclude_none=True`, so a value the
// backend does not have is absent, never null.
//
// Reason labels and categories come from the response (leads/rejected.py's
// REASON_LABELS / REASON_CATEGORY). This screen never names a reason itself,
// so utils/rejections.js is not used here.
import { FALLBACK_TIMEZONE } from '../staff-leads/model'
import { parseUrlRange, rangeParams, rangeSearch } from '../../utils/dateRange'

export { FALLBACK_TIMEZONE }

export const ENDPOINT = '/admin/leads/blocked'
export const detailEndpoint = (eventId) => `${ENDPOINT}/${encodeURIComponent(eventId)}`

// A range is a preset id below or a custom `{ from, to }` (utils/dateRange).
export const DEFAULT_RANGE = 'today'

// BlockedRange in leads/blocked_at_intake.py, less `custom`, which
// RangeSelect's Custom button sends with date_from / date_to.
export const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
]

const n = (v) => Number(v || 0)

// ── categories ──────────────────────────────────────────────────────────

// REASON_CATEGORY's five categories, in the order the daily bars stack them
// (bottom first). An unknown category reads as `other`.
export const CATEGORIES = [
  { id: 'duplicate', label: 'Duplicate', color: 'var(--red)', bg: 'var(--red-bg)', fg: 'var(--red)' },
  { id: 'data_error', label: 'Data error', color: 'var(--amber)', bg: 'var(--amber-bg)', fg: 'var(--amber)' },
  { id: 'compliance', label: 'Compliance', color: 'var(--text-muted)', bg: 'var(--bg-raised)', fg: 'var(--text-secondary)' },
  { id: 'account', label: 'Account', color: 'var(--blue)', bg: 'var(--blue-bg)', fg: 'var(--blue)' },
  { id: 'other', label: 'Other', color: 'var(--border-strong)', bg: 'var(--bg-raised)', fg: 'var(--text-muted)' },
]
export const categoryMeta = (category) => CATEGORIES.find((c) => c.id === category) || CATEGORIES[CATEGORIES.length - 1]

// ── numbers ─────────────────────────────────────────────────────────────

export const count = (v) => n(v).toLocaleString('en-US')

// A fraction as "42.5%"; an absent rate (nothing to divide by) reads "—".
export function rate(fraction) {
  if (fraction === null || fraction === undefined) return '—'
  return `${(Number(fraction) * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`
}

// More blocked is worse, so the colours are the Home's turned around: up is
// red, down is green.
export const BLOCKED_DELTA_COLOR = { up: 'var(--red)', down: 'var(--green)', flat: 'var(--text-muted)' }

// "12.3% of 1,234 submissions". Under a reason filter `blocked` narrows but
// `submissions` does not (the backend's rule), so the share is of all
// traffic and the caption says so.
export function blockedCaption(kpis, reasonFiltered) {
  const submissions = kpis?.submissions?.value
  const share = kpis?.block_rate?.value
  if (!n(submissions) || share === undefined || share === null) return 'No submissions in this range'
  return `${rate(share)} of ${reasonFiltered ? 'all ' : ''}${count(submissions)} submissions`
}

// The "Missing or invalid fields" chips: every reason's fields added up by
// field and kind, most frequent first. The backend records only the first
// invalid field of a submission, so these are floors, not totals.
export function fieldChips(reasons = []) {
  const totals = new Map()
  for (const r of reasons) {
    for (const f of r.fields || []) {
      const key = `${f.kind}|${f.field}`
      const prev = totals.get(key)
      totals.set(key, { field: f.field, kind: f.kind, count: (prev?.count || 0) + n(f.count) })
    }
  }
  return [...totals.values()].sort((a, b) => b.count - a.count || a.field.localeCompare(b.field))
}

// One day of the daily series as stacked segments, bottom first.
export const daySegments = (day) => CATEGORIES.map((c) => ({ ...c, value: n(day?.[c.id]) }))
export const dayTotal = (day) => CATEGORIES.reduce((sum, c) => sum + n(day?.[c.id]), 0)

// ── request ─────────────────────────────────────────────────────────────

const csv = (values) => (values && values.length ? values.join(',') : undefined)
const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''))

export const requestParams = ({ range, partners, reasons, page }) =>
  clean({ ...rangeParams(range), partners: csv(partners), reasons: csv(reasons), page: page > 1 ? page : undefined })

// ── filters in the URL ──────────────────────────────────────────────────

const listFrom = (value) => (value ? String(value).split(',').map((v) => v.trim()).filter(Boolean) : [])

// Range and page are checked against what this screen knows. Partners and
// reasons come out as candidates only: allowedFilters() checks them against
// the first response's filter_options before any request carries them.
export function parseUrlFilters(searchParams) {
  const page = Number(searchParams.get('page'))
  return {
    range: parseUrlRange(searchParams, RANGE_PRESETS, DEFAULT_RANGE),
    page: Number.isInteger(page) && page > 1 ? page : 1,
    partners: listFrom(searchParams.get('partners')),
    reasons: listFrom(searchParams.get('reasons')),
  }
}

// Only what filter_options offers survives: an unknown id is never sent,
// never shown as selected, and never written back into the URL.
export function allowedFilters({ partners = [], reasons = [] }, options) {
  const partnerIds = new Set((options?.partners || []).filter((p) => p.id !== undefined && p.id !== null).map((p) => String(p.id)))
  const reasonCodes = new Set((options?.reasons || []).map((r) => r.code))
  return {
    partners: [...new Set(partners.map(String))].filter((id) => partnerIds.has(id)),
    reasons: [...new Set(reasons)].filter((code) => reasonCodes.has(code)),
  }
}

// The query string for a state, defaults left out.
export const filtersToSearch = ({ range, partners, reasons, page }) =>
  clean({
    ...rangeSearch(range, DEFAULT_RANGE),
    partners: csv(partners),
    reasons: csv(reasons),
    page: page > 1 ? String(page) : undefined,
  })

export const BLOCKED_RESPONSIVE_CSS = `
/* No backticks in these comments: this lives inside a JS template literal. */
.bk-kpis { display: grid; gap: 14px; grid-template-columns: repeat(4, minmax(0, 1fr)); }
.bk-split { display: grid; gap: 16px; grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); align-items: start; }
.bk-card-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.bk-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.bk-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.bk-table th { text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); padding: 8px 10px; border-bottom: 1px solid var(--border); white-space: nowrap; }
.bk-table td { padding: 9px 10px; border-bottom: 1px solid var(--border); color: var(--text-primary); vertical-align: middle; }
.bk-table tr:last-child td { border-bottom: none; }
.bk-table .bk-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.bk-row-spike td { background: var(--red-bg); }
.bk-reason { display: flex; flex-direction: column; gap: 4px; width: 100%; text-align: left; background: none; border: 1px solid transparent; border-radius: 8px; padding: 6px 8px; font: inherit; color: var(--text-primary); cursor: pointer; transition: background .12s, border-color .12s; }
.bk-reason:hover { background: var(--bg-raised); }
.bk-reason[aria-pressed="true"] { border-color: var(--accent); background: var(--accent-soft); }
.bk-track { height: 8px; border-radius: 99px; background: var(--bg-raised); overflow: hidden; }
.bk-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 9px; border-radius: 999px; font-size: 12px; border: 1px solid var(--border); background: var(--bg-surface); color: var(--text-secondary); }
.bk-pill { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.bk-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.bk-day-bars { display: grid; grid-template-columns: repeat(14, minmax(0, 1fr)); gap: 6px; align-items: end; height: 180px; position: relative; }
.bk-day-short { display: none; }
.bk-day-labels { display: grid; grid-template-columns: repeat(14, minmax(0, 1fr)); gap: 6px; border-top: 1px solid var(--border-strong); padding-top: 4px; }
.bk-filter { position: relative; }
.bk-filter > summary { list-style: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; font-size: 12px; font-weight: 600; border-radius: 6px; border: 1px solid var(--border); color: var(--text-secondary); background: var(--bg-surface); white-space: nowrap; }
.bk-filter > summary::-webkit-details-marker { display: none; }
.bk-filter[data-active="true"] > summary { border-color: var(--accent); color: var(--accent); background: var(--accent-soft); }
.bk-filter-menu { position: absolute; right: 0; top: calc(100% + 4px); z-index: 20; min-width: 220px; max-height: 320px; overflow: auto; padding: 6px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-surface); box-shadow: var(--shadow-lg); display: flex; flex-direction: column; gap: 2px; }
.bk-filter-menu label { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 6px; font-size: 13px; color: var(--text-primary); cursor: pointer; }
.bk-filter-menu label:hover { background: var(--bg-raised); }
.bk-panel-backdrop { position: fixed; inset: 0; z-index: 900; background: var(--bg-overlay); }
.bk-panel { position: fixed; top: 0; right: 0; bottom: 0; z-index: 901; width: min(520px, 100vw); overflow-y: auto; background: var(--bg-surface); border-left: 1px solid var(--border); box-shadow: var(--shadow-lg); padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.bk-kv { display: grid; grid-template-columns: 120px minmax(0, 1fr); font-size: 13px; margin: 0; }
.bk-kv dt { color: var(--text-muted); padding: 7px 12px 7px 0; border-bottom: 1px solid var(--border); }
.bk-kv dd { margin: 0; padding: 7px 0; border-bottom: 1px solid var(--border); color: var(--text-primary); overflow-wrap: anywhere; }
/* JsonViewer (components/lead-inspector) styles itself with these. */
.li-json { margin: 0; padding: 12px; max-height: 420px; overflow: auto; font-size: 12px; line-height: 1.5; border-radius: 8px; background: var(--bg-raised); border: 1px solid var(--border); color: var(--text-primary); white-space: pre-wrap; overflow-wrap: anywhere; }
.li-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.li-toggle { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; padding: 0; font: inherit; font-size: 13px; font-weight: 600; color: var(--text-primary); cursor: pointer; }
.bk-reason:focus-visible, .bk-day:focus-visible, .bk-filter > summary:focus-visible, .li-toggle:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.bk-skeleton { background: var(--bg-raised); border-radius: 6px; animation: bk-pulse 1.2s ease-in-out infinite; }
@keyframes bk-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
.bk-spin { animation: bk-spin 0.9s linear infinite; }
@keyframes bk-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .bk-skeleton, .bk-spin { animation: none; } }
@media (max-width: 1100px) {
  .bk-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .bk-split { grid-template-columns: 1fr !important; }
}
@media (max-width: 768px) {
  .bk-header { flex-direction: column !important; align-items: stretch !important; }
  .bk-header-actions { justify-content: flex-start !important; }
  .bk-filter-menu { left: 0; right: auto; }
  .bk-day-bars, .bk-day-labels { gap: 3px; }
  .bk-day-long { display: none; }
  .bk-day-short { display: inline; }
  .bk-hide-sm { display: none !important; }
}
@media (max-width: 480px) {
  .bk-kpis { grid-template-columns: 1fr !important; }
}
`
